import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { TopBar } from "@/components/TopBar";
import { motion } from "framer-motion";
import { ArrowLeft, GraduationCap, BookOpen, CheckCircle2, RotateCw, Play, Cross, Calculator } from "lucide-react";

const COURSE_META = {
  "Systematic Theology": { icon: Cross, color: "#3D405B", blurb: "Wayne Grudem — the doctrines of the faith" },
  "QuickBooks Online": { icon: Calculator, color: "#81B29A", blurb: "Practical bookkeeping, step by step" },
};

function StatusPill({ status, percentage }) {
  const map = {
    "Not Started": { label: "Not started", color: "#6B7280", bg: "#F2EFE9" },
    "In Progress": { label: "In progress", color: "#457B9D", bg: "#A8DADC33" },
    "Submitted": { label: "Submitted", color: "#457B9D", bg: "#A8DADC33" },
    "Needs Review": { label: "Needs review", color: "#F4A261", bg: "#F4A26122" },
    "Graded": { label: `Scored ${percentage}%`, color: "#81B29A", bg: "#81B29A22" },
  };
  const s = map[status] || map["Not Started"];
  return <span className="stat text-xs font-semibold px-2.5 py-1 rounded-full" style={{ color: s.color, background: s.bg }}>{s.label}</span>;
}

export default function ContinuingEducation() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { api.get("/my/continuing-education").then((r) => setItems(r.data)).finally(() => setLoading(false)); }, []);

  const courses = {};
  items.forEach((a) => { (courses[a.course] ||= []).push(a); });

  const cta = (status) => status === "Graded" ? { label: "Review", icon: BookOpen }
    : status === "In Progress" ? { label: "Continue", icon: RotateCw }
    : { label: "Start", icon: Play };

  return (
    <div>
      <TopBar subtitle="Continuing Education" />
      <main className="max-w-4xl mx-auto px-6 md:px-8 py-10">
        <button onClick={() => navigate("/parent")} className="flex items-center gap-2 text-sm text-[#6B7280] hover:text-[#3D405B] mb-6"><ArrowLeft size={16} /> Dashboard</button>
        <div className="flex items-center gap-3 mb-8">
          <span className="grid place-items-center w-11 h-11 rounded-2xl bg-[#E07A5F] text-white"><GraduationCap size={20} /></span>
          <div>
            <h1 className="font-head text-4xl font-bold text-[#3D405B]">My Continuing Education</h1>
            <p className="text-[#6B7280] text-sm">Your own courses — take a lesson, answer, and get graded. Only you can see your progress.</p>
          </div>
        </div>

        {loading ? <p className="text-[#6B7280]">Loading…</p> : items.length === 0 ? (
          <p className="text-[#6B7280]" data-testid="ce-empty">No courses assigned to you yet.</p>
        ) : (
          <div className="space-y-8">
            {Object.entries(courses).map(([course, lessons]) => {
              const meta = COURSE_META[course] || { icon: BookOpen, color: "#81B29A", blurb: "" };
              const Icon = meta.icon;
              const done = lessons.filter((l) => l.status === "Graded").length;
              return (
                <div key={course} data-testid={`ce-course-${course}`}>
                  <div className="flex items-center gap-3 mb-3">
                    <span className="grid place-items-center w-9 h-9 rounded-xl text-white shrink-0" style={{ background: meta.color }}><Icon size={17} /></span>
                    <div>
                      <h2 className="font-head text-xl font-bold text-[#3D405B]">{course}</h2>
                      <p className="text-xs text-[#6B7280]">{meta.blurb} · {done}/{lessons.length} complete</p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {lessons.map((l, i) => {
                      const c = cta(l.status);
                      const CIcon = c.icon;
                      return (
                        <motion.div key={l.id} data-testid={`ce-lesson-${l.id}`}
                          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i * 0.04, 0.3) }}
                          className="flex items-center justify-between gap-4 rounded-2xl bg-white border border-[#EAE6DF] px-5 py-4">
                          <div className="flex items-center gap-3 min-w-0">
                            <span className="grid place-items-center w-8 h-8 rounded-full text-sm font-semibold shrink-0"
                              style={{ background: l.status === "Graded" ? "#81B29A" : "#F2EFE9", color: l.status === "Graded" ? "#fff" : "#6B7280" }}>
                              {l.status === "Graded" ? <CheckCircle2 size={16} /> : i + 1}
                            </span>
                            <div className="min-w-0">
                              <p className="font-head font-semibold text-[#3D405B] truncate">{l.title}</p>
                              <div className="mt-0.5"><StatusPill status={l.status} percentage={l.percentage} /></div>
                            </div>
                          </div>
                          <button data-testid={`ce-open-${l.id}`} onClick={() => navigate(`/parent/lesson/${l.id}`)}
                            className="shrink-0 flex items-center gap-1.5 rounded-full bg-[#E07A5F] text-white px-5 py-2 text-sm font-semibold hover:bg-[#c96851] transition-colors">
                            <CIcon size={15} /> {c.label}
                          </button>
                        </motion.div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
